import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { useAdminAuth } from "./AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Package, Plus, Edit, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminBundles() {
  const { token } = useAdminAuth();
  const [bundles, setBundles] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBundle, setEditingBundle] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("1L");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [discountType, setDiscountType] = useState("fixed_percentage");
  const [discountValue, setDiscountValue] = useState("");
  const [thumbnailUrl, setThumbnailUrl] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bundlesRes, productsRes] = await Promise.all([
        axios.get(`${API}/admin/bundles`, {
          
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }),
        axios.get(`${API}/admin/products`, {
          
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        })
      ]);
      setBundles(bundlesRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error("Failed to fetch data", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setCategory("1L");
    setSelectedProducts([]);
    setDiscountType("fixed_percentage");
    setDiscountValue("");
    setThumbnailUrl("");
    setEditingBundle(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (bundle) => {
    setEditingBundle(bundle);
    setTitle(bundle.title);
    setDescription(bundle.description);
    setCategory(bundle.category);
    setSelectedProducts(bundle.product_ids || []);
    setDiscountType(bundle.discount_type);
    setDiscountValue(bundle.discount_value.toString());
    setThumbnailUrl(bundle.thumbnail_url || "");
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const bundleData = {
      title,
      description,
      category,
      product_ids: selectedProducts,
      discount_type: discountType,
      discount_value: parseFloat(discountValue),
      thumbnail_url: thumbnailUrl || null
    };

    try {
      if (editingBundle) {
        await axios.put(`${API}/admin/bundles/${editingBundle.bundle_id}`, bundleData, {
          
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        toast.success("Bundle updated");
      } else {
        await axios.post(`${API}/admin/bundles`, bundleData, {
          
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        toast.success("Bundle created");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save bundle");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bundleId) => {
    if (!confirm("Are you sure you want to delete this bundle?")) return;

    try {
      await axios.delete(`${API}/admin/bundles/${bundleId}`, {
        
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Bundle deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete bundle");
    }
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const categoryColors = {
    "1L": "bg-emerald-900/50 text-emerald-300",
    "2L": "bg-blue-900/50 text-blue-300",
    "3L": "bg-purple-900/50 text-purple-300",
    "bar_exam": "bg-secondary text-secondary-foreground",
    "all": "bg-gold/20 text-gold"
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Package className="h-8 w-8 text-gold" />
          <h1 className="font-serif text-2xl font-bold">Bundles</h1>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gold text-primary-foreground hover:bg-gold/90" onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Add Bundle
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingBundle ? "Edit Bundle" : "Create Bundle"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Contracts Complete Course"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="All contract law topics in one bundle"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1L">1L</SelectItem>
                      <SelectItem value="2L">2L</SelectItem>
                      <SelectItem value="3L">3L</SelectItem>
                      <SelectItem value="bar_exam">Bar Exam</SelectItem>
                      <SelectItem value="all">All Access</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Discount Type</Label>
                  <Select value={discountType} onValueChange={setDiscountType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed_percentage">Percentage Off</SelectItem>
                      <SelectItem value="custom_price">Custom Price</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="discountValue">
                  {discountType === "fixed_percentage" ? "Discount %" : "Bundle Price ($)"}
                </Label>
                <Input
                  id="discountValue"
                  type="number"
                  step="0.01"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  placeholder={discountType === "fixed_percentage" ? "30" : "99.99"}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="thumbnailUrl">Thumbnail URL (optional)</Label>
                <Input
                  id="thumbnailUrl"
                  value={thumbnailUrl}
                  onChange={(e) => setThumbnailUrl(e.target.value)}
                  placeholder="https://..."
                />
              </div>

              <div className="space-y-2">
                <Label>Select Topics ({selectedProducts.length} selected)</Label>
                <div className="max-h-48 overflow-y-auto border border-border rounded-lg p-2 space-y-1">
                  {products.map((product) => (
                    <label
                      key={product.product_id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted ${
                        selectedProducts.includes(product.product_id) ? 'bg-gold/10' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedProducts.includes(product.product_id)}
                        onChange={() => toggleProductSelection(product.product_id)}
                        className="rounded"
                      />
                      <span className="text-sm flex-1">{product.title}</span>
                      <Badge variant="outline" className="text-xs">{product.category}</Badge>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-gold text-primary-foreground" disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingBundle ? "Update" : "Create")}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="grid gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      ) : bundles.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="p-12 text-center">
            <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-serif text-lg font-semibold mb-2">No bundles yet</h3>
            <p className="text-muted-foreground">Create your first bundle to start selling</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {bundles.map((bundle) => (
            <Card key={bundle.bundle_id} className="bg-card border-border">
              <CardContent className="p-4 flex items-center gap-4">
                {bundle.thumbnail_url && (
                  <img 
                    src={bundle.thumbnail_url} 
                    alt={bundle.title}
                    className="w-16 h-16 rounded object-cover"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium">{bundle.title}</h3>
                    <Badge className={categoryColors[bundle.category] || "bg-muted"}>
                      {bundle.category}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-1">
                    {bundle.product_ids?.length || 0} topics • 
                    {bundle.discount_type === "fixed_percentage" 
                      ? ` ${bundle.discount_value}% off`
                      : ` $${bundle.discount_value}`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gold">${bundle.final_price?.toFixed(2)}</p>
                  {bundle.original_price > bundle.final_price && (
                    <p className="text-sm text-muted-foreground line-through">${bundle.original_price?.toFixed(2)}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="icon" onClick={() => openEditDialog(bundle)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(bundle.bundle_id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
